import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useDialog } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { ShieldCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { OTPFormValues, otpSchema } from '../schema';

interface OtpFormProps {
  onVerify: (data: OTPFormValues) => void;
}

export function EnterOtp({ onVerify }: OtpFormProps) {
  const { t } = useTranslation();
  const { isOpen, onClose } = useDialog();

  const form = useForm<OTPFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: ''
    }
  });

  const onSubmit = (data: OTPFormValues) => {
    onVerify(data);
    form.setValue('otp', '');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-[560px] gap-0 overflow-hidden rounded-[24px] border border-[#e2e9f3] bg-white p-0  ">
        <DialogHeader className="items-center border-b  p-4 text-center sm:px-10 sm:text-center">
          <DialogTitle className="text-md font-semibold tracking-[-0.035em] text-[#172641] sm:text-[18px]">
            {t('TextVerify')}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-7 px-6 py-8 sm:px-10 sm:py-9"
          >
            <FormField
              control={form.control}
              name="otp"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="block text-center text-sm font-semibold text-[#34435d]">
                    {t('TextOneTimePassword')}
                  </FormLabel>
                  <FormControl>
                    <InputOTP maxLength={6} {...field} containerClassName="w-full justify-center">
                      <InputOTPGroup className="gap-2 sm:gap-2.5">
                        {[0, 1, 2, 3, 4, 5].map((index) => (
                          <InputOTPSlot
                            key={index}
                            index={index}
                            className="h-10 w-10 border border-gray-200"
                          />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <FormDescription className="text-center text-xs leading-5 text-[#8a97aa]">
                    {t('POneTimePassword')}
                  </FormDescription>
                  <FormMessage className="text-center" />
                </FormItem>
              )}
            />
            <div className="flex items-center justify-center">
              <Button className=" bg-[#338AF3] hover:bg-blue-800" type="submit">
                <ShieldCheck className="mr-2 h-5 w-5" />
                {t('TextSubmit')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
